/**
 * UI-8d Task 6 — usePlatformChangeLog hook.
 *
 * Read-only paginated fetcher over GET `/platform/change-log`
 * (apps/api/src/routes/platform/change-log/list.ts). Mirrors the
 * pagination + filter shape of `useAuditLog` / `usePlatformAuditLog`,
 * but the row type is `ChangeEntry` (the SOX-ITGC change-management
 * record) and the filter set differs: `changeType`, `requestedBy`,
 * `approvedBy`, `from`, `to`.
 *
 * Backend envelope is the standard `{ rows, total, limit, offset }`.
 *
 * Pagination contract is identical to the platform audit hook:
 *   - `limit` fixed at 50/page.
 *   - `loadNextPage()` increments offset by 50 and APPENDS rows.
 *   - Filter changes reset offset to 0 and replace rows.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api/client.js'

export interface ChangeEntry {
  id: string
  changeType: string
  description: string
  requestedBy: string
  approvedBy: string | null
  implementedAt: string
  beforeState: unknown
  afterState: unknown
  rollbackPlan: string | null
}

export interface ChangeFilters {
  changeType?: string
  requestedBy?: string
  approvedBy?: string
  from?: Date
  to?: Date
}

export interface UsePlatformChangeLogResult {
  rows: ChangeEntry[]
  total: number
  loading: boolean
  error: string | null
  loadNextPage: () => Promise<void>
  refetch: () => Promise<void>
}

interface ChangeLogResponse {
  rows: ChangeEntry[]
  total: number
  limit: number
  offset: number
}

const PAGE_SIZE = 50

function buildPath(filters: ChangeFilters, offset: number): string {
  const params = new URLSearchParams()
  if (filters.changeType) params.set('changeType', filters.changeType)
  if (filters.requestedBy) params.set('requestedBy', filters.requestedBy)
  if (filters.approvedBy) params.set('approvedBy', filters.approvedBy)
  if (filters.from) params.set('from', filters.from.toISOString())
  if (filters.to) params.set('to', filters.to.toISOString())
  params.set('limit', String(PAGE_SIZE))
  params.set('offset', String(offset))
  return `/platform/change-log?${params.toString()}`
}

export function usePlatformChangeLog(
  filters: ChangeFilters,
): UsePlatformChangeLogResult {
  const [rows, setRows] = useState<ChangeEntry[]>([])
  const [total, setTotal] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // Primitive filter keys so the effect deps don't churn on every render.
  const changeTypeKey = filters.changeType ?? ''
  const requestedByKey = filters.requestedBy ?? ''
  const approvedByKey = filters.approvedBy ?? ''
  const fromKey = filters.from ? filters.from.toISOString() : ''
  const toKey = filters.to ? filters.to.toISOString() : ''

  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const filtersRef = useRef(filters)
  filtersRef.current = filters

  const offsetRef = useRef(0)

  const fetchPage = useCallback(
    async (offset: number): Promise<ChangeLogResponse> => {
      return api.get<ChangeLogResponse>(buildPath(filtersRef.current, offset))
    },
    [],
  )

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const resp = await fetchPage(0)
      if (mountedRef.current) {
        setRows(resp.rows)
        setTotal(resp.total)
        offsetRef.current = PAGE_SIZE
      }
    } catch (e) {
      if (mountedRef.current) {
        setError(e instanceof Error ? e.message : 'Failed to load change log')
        setRows([])
        setTotal(0)
      }
    } finally {
      if (mountedRef.current) setLoading(false)
    }
  }, [fetchPage])

  const loadNextPage = useCallback(async (): Promise<void> => {
    if (loading) return
    setLoading(true)
    setError(null)
    try {
      const resp = await fetchPage(offsetRef.current)
      if (mountedRef.current) {
        setRows((prev) => [...prev, ...resp.rows])
        setTotal(resp.total)
        offsetRef.current += PAGE_SIZE
      }
    } catch (e) {
      if (mountedRef.current) {
        setError(e instanceof Error ? e.message : 'Failed to load change log')
      }
    } finally {
      if (mountedRef.current) setLoading(false)
    }
  }, [fetchPage, loading])

  // Reset + refetch on any filter change.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    offsetRef.current = 0
    api
      .get<ChangeLogResponse>(buildPath(filtersRef.current, 0))
      .then((resp) => {
        if (cancelled) return
        setRows(resp.rows)
        setTotal(resp.total)
        offsetRef.current = PAGE_SIZE
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(e instanceof Error ? e.message : 'Failed to load change log')
        setRows([])
        setTotal(0)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return (): void => {
      cancelled = true
    }
  }, [changeTypeKey, requestedByKey, approvedByKey, fromKey, toKey])

  return { rows, total, loading, error, loadNextPage, refetch }
}
